import type {
  InsertionOrderMetricRow,
  LineItemMetricRow,
  DomainMetricRow,
  TopUrlRow,
  UrlMetricRow,
} from '@monitoring-panel/types';
import type { ReportColumn } from './ReportTable';
import { formatCount, formatInt, formatPercent } from '@/lib/report-format';

/** Truncated URL cell with domain on top and full url below. */
function urlCell(domain: string, url: string) {
  return (
    <div className="min-w-0">
      <span className="font-bold text-adv-primary">{domain}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="adv-link block text-adv-body-s break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    </div>
  );
}

export const insertionOrderColumns: ReportColumn<InsertionOrderMetricRow>[] = [
  { key: 'insertion_order_id', label: 'Insertion order' },
  { key: 'line_items_count', label: 'Line items', align: 'right', render: (r) => formatInt(r.line_items_count) },
  { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
  { key: 'clicks_count', label: 'Clicks', align: 'right', render: (r) => formatCount(r.clicks_count) },
  { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
  { key: 'viewability_rate', label: 'Viewability', align: 'right', render: (r) => formatPercent(r.viewability_rate) },
  { key: 'total_conversions', label: 'Conv.', align: 'right', render: (r) => formatInt(r.total_conversions) },
  { key: 'domains_count', label: 'Domains', align: 'right', render: (r) => formatInt(r.domains_count) },
];

export const lineItemColumns: ReportColumn<LineItemMetricRow>[] = [
  { key: 'line_item_id', label: 'Line item' },
  { key: 'insertion_order_id', label: 'IO' },
  { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
  { key: 'clicks_count', label: 'Clicks', align: 'right', render: (r) => formatCount(r.clicks_count) },
  { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
  { key: 'viewability_rate', label: 'Viewability', align: 'right', render: (r) => formatPercent(r.viewability_rate) },
  { key: 'total_conversions', label: 'Conv.', align: 'right', render: (r) => formatInt(r.total_conversions) },
  { key: 'urls_count', label: 'URLs', align: 'right', render: (r) => formatInt(r.urls_count) },
];

export const domainColumns: ReportColumn<DomainMetricRow>[] = [
  { key: 'domain', label: 'Domain' },
  { key: 'urls_count', label: 'URLs', align: 'right', render: (r) => formatInt(r.urls_count) },
  { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
  { key: 'clicks_count', label: 'Clicks', align: 'right', render: (r) => formatCount(r.clicks_count) },
  { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
  { key: 'viewability_rate', label: 'Viewability', align: 'right', render: (r) => formatPercent(r.viewability_rate) },
  { key: 'total_conversions', label: 'Conv.', align: 'right', render: (r) => formatInt(r.total_conversions) },
];

export const topUrlColumns: ReportColumn<TopUrlRow>[] = [
  { key: 'url', label: 'URL', wrap: true, render: (r) => urlCell(r.domain, r.url) },
  { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
  { key: 'clicks_count', label: 'Clicks', align: 'right', render: (r) => formatCount(r.clicks_count) },
  { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
];

export const urlMetricColumns: ReportColumn<UrlMetricRow>[] = [
  { key: 'url', label: 'URL', wrap: true, render: (r) => urlCell(r.domain, r.url) },
  { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
  { key: 'clicks_count', label: 'Clicks', align: 'right', render: (r) => formatCount(r.clicks_count) },
  { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
  { key: 'viewability_rate', label: 'Viewability', align: 'right', render: (r) => formatPercent(r.viewability_rate) },
  { key: 'total_conversions', label: 'Conv.', align: 'right', render: (r) => formatInt(r.total_conversions) },
  { key: 'line_items_count', label: 'Line items', align: 'right', render: (r) => formatInt(r.line_items_count) },
];
